import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentsProcess, updateMailToReceiveCheck } from '../../stores/payment.store';
import { useTranslation } from 'react-i18next';
import { PaymentMethodCardSvg, ProviderSelectActiveSvg, ProviderSelectNoActiveSvg } from '../../icons';
import { MainContext } from '../../context/main.context';
import { getAuthElemStyle, isEmail, setFocus } from '../../common/tools';
import { PAYMENTS_PROCESS_ERROR, UPDATE_MAIL_TO_RECEIVE_CHECK_ERROR } from '../../common/errorCodes';
import { toast } from 'react-toastify';
import { PROVIDER_LIST, STATIC_FILES_PATH } from '../../common/constants';
import Header from '../header';
import Footer from '../footer';
import ManualController from '../../hoc/ManualController';
import { IPaymentDataDto, IPaymentsMethods, IProfileDataDto, IProvider } from '../../interfaces';
import PaymentsYookassa from './yookassa';
import PaymentsStripe from './stripe';
import PaymentsYandexPay from './yandex_pay';


export default function Payments() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [ data, setData ] = useState<IPaymentDataDto>();
    const [ profileData, setProfileData ] = useState<IProfileDataDto>();
    const [ email, setEmail ] = useState<string>('');
    const { manualRedirect } = ManualController();
    const [ emailErr , setEmailErr ] = useState<string>('');
    const {
        settings,
        modalContent,
        paymentId,
        locale,
        project,
        source,
        jwt,
        toggleLoader,
        setModalContent,
    } = useContext(MainContext);
    const [ activeProvider, setActiveProvider ] = useState<string | undefined>(settings?.projects?.paymentsMethods?.[0]);

    useEffect(() => _paymentsProcess(), []);

    const _createPayment = () => {
        if (activeProvider === 'yookassa') {
            setModalContent({ component: <PaymentsYookassa/> });
            return;
        }

        if (activeProvider === 'stripe') {
            setModalContent({ component: <PaymentsStripe/> });
            return;
        }

        if (activeProvider === 'yandex-pay') {
            setModalContent({ component: <PaymentsYandexPay/> });
            return;
        }
    }

    const _navigate = (path: string): void => {
        navigate({
            pathname: path,
            search: `?id=${paymentId}&source=${source}&locale=${locale}&project=${project}&jwt=${jwt}`,
        });
    }

    const _paymentsProcess = (): void => {
        toggleLoader(true);

        paymentsProcess(paymentId).then(res => {
            const { provider, providerId, paymentData, profileData } = res?.data;

            if (!provider || !providerId) {
                setData(paymentData);
                setProfileData(profileData);
                setEmail(profileData.mailToReceiveCheck);
            } else {
                if (modalContent) {
                    _createPayment();
                    return;
                }

                _navigate(`/payments/${provider}`);
            }
        }, err => {
            const errData = err?.response?.data || err;

            manualRedirect(errData, 'payment', true, PAYMENTS_PROCESS_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (!email) {
            setFocus('payment-email-id');
            setEmailErr(t('login.enterEmail'));
            return;
        }

        if (!isEmail(email)) {
            setFocus('payment-email-id');
            setEmailErr(t('login.invalidEmail'));
            return;
        }

        if (emailErr) {
            setEmailErr('');
        }

        const navigatePath: string = `/payments/${activeProvider}`;
        const oldEmail: string = profileData?.['mailToReceiveCheck'] || '';

        if (oldEmail !== email) {
            _updateMailToReceiveCheck(email, navigatePath);
        } else {
            if (modalContent) {
                _createPayment();
                return;
            }

            _navigate(navigatePath);
        }
    }

    const _updateMailToReceiveCheck = (email: string, navigatePath: string): void => {
        toggleLoader(true);

        updateMailToReceiveCheck(email).then(res => {
            const result = res?.data?.result;

            if (result) {
                if (modalContent) {
                    _createPayment();
                    return;
                }

                _navigate(navigatePath);
                return;
            }

            manualRedirect(res, 'payment', true, UPDATE_MAIL_TO_RECEIVE_CHECK_ERROR);
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'payment', true, UPDATE_MAIL_TO_RECEIVE_CHECK_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _renderMenu = (): React.JSX.Element => {
        return (
            <div className="auth-modal-menu">
                {_renderPaymentBodyProjectInfo()}
                {_renderPaymentBodyInfo()}
                {_renderPaymentBodyProviders()}
                {_renderPaymentBodyButton()}
            </div>
        );
    }

    const _renderPaymentBodyProjectInfo = () => {
        const projectImageSrc =
            `${STATIC_FILES_PATH}${data?.cardLogoImage}`;

        return (
            <div className="payments-body-project-info">
                <div className="payments-body-project-info-img-box">
                    <img className="payments-body-project-info-img" alt="" src={projectImageSrc}/>
                </div>
                <div className="payments-body-project-info-name">
                    {data?.projectName}
                </div>
            </div>
        );
    }

    const _renderPaymentBodyInfo = (): React.JSX.Element => {
        const title: string = _getTitle();

        return (
            <div className="payments-body-info">
                <div className="payments-body-info-title text-overflow" title={title}>
                    {title}
                </div>
                <div className="payments-body-info-sum">
                    <div className="payments-body-info-sum-pay">
                        {t('payment.toPay')}
                    </div>
                    <div className="payments-body-info-currency">
                        {_getCurrency()}
                    </div>
                </div>
            </div>
        );
    }

    const _renderPaymentBodyProviders = () => {
        const paymentsMethods: IPaymentsMethods[] | undefined = settings?.projects?.paymentsMethods;

        if (!paymentsMethods?.length) {
            toast.error('PAYMENTS_METHODS_NOT_EXIST');
            console.log('PAYMENTS_METHODS_NOT_EXIST', { settings });
            return null;
        }

        return (
            <div className="payments-body-providers">
                {
                    paymentsMethods.map((provider: IPaymentsMethods, index: number) => {
                        const key: string = `${provider}_${index}`;
                        const imageSrc: string = `${window.location.origin}/provider_images/${provider}.webp`;

                        return (
                            <div
                                key={key}
                                className="payments-body-provider-box"
                                style={{opacity: provider === activeProvider ? '1' : '0.5'}}
                                onClick={() => setActiveProvider(provider)}
                            >
                                <div className="payments-body-provider-box-top">
                                    <div className="payments-body-provider-box-top-item-image">
                                        <img
                                            className="payments-body-provider-box-top-item-image"
                                            alt=""
                                            src={imageSrc}
                                        />
                                    </div>
                                    <div className="payments-body-provider-box-top-item-select">
                                        {
                                            provider === activeProvider ?
                                                <ProviderSelectActiveSvg/>
                                            :
                                                <ProviderSelectNoActiveSvg/>
                                        }
                                    </div>
                                </div>
                                <div className="payments-body-provider-box-center text-overflow">
                                    {_getProviderName(provider)}
                                </div>
                                <div className="payments-body-provider-box-bottom">
                                    <div className="payments-body-provider-box-bottom-title text-overflow">
                                        {t('payment.method')}:
                                    </div>
                                    {_renderProviderPaymentsMethod(provider)}
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        );
    }

    const _renderPaymentBodyButton = (): React.JSX.Element => {
        return (
            <form
                className="payments-body-form"
                noValidate
                onSubmit={(e) => _onSubmit(e)}
            >
                <div className="login-modal-menu-body-input">
                    <div className="login-modal-menu-body-input-title text-overflow">
                        {t('payment.email')}
                    </div>
                    <input
                        className="login-modal-menu-body-input-item"
                        type="email"
                        id="payment-email-id"
                        autoComplete="off"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{border: `1px solid ${emailErr ? '#B01E1E' : '#d1d1d0'}`}}
                    >
                    </input>
                    { emailErr ? <div className="req-input-err">{emailErr}</div> : null }
                </div>
                <button className="btn" type="submit">
                    <div className="payments-body-info-currency-btn">
                        {_getCurrency()}
                    </div>
                </button>
            </form>
        );
    };

    const _getCurrency = (): string => {
        let currencyData: string = '';

        const currProviderData: IProvider | undefined
            = PROVIDER_LIST.find((provider: IProvider) => provider.id === activeProvider);

        if (!currProviderData) {
            toast.error('PAYMENTS_PROVIDER_DATA_NOT_EXIST');
            console.log('PAYMENTS_PROVIDER_DATA_NOT_EXIST');
            return '';
        }

        const { fromCharCode, currency } = currProviderData;

        if (currency === 'RUB') {
            currencyData = `${data?.ruPrice}${fromCharCode}`;
        }

        if (currency === 'USD') {
            currencyData = `${data?.enPrice}${fromCharCode}`;
        }

        return currencyData;
    };

    const _getTitle = (): string => {
        let title: string = '';

        const currProviderData: IProvider | undefined
            = PROVIDER_LIST.find((provider: IProvider) => provider.id === activeProvider);

        if (!currProviderData) {
            toast.error('PAYMENTS_PROVIDER_DATA_NOT_EXIST');
            console.log('PAYMENTS_PROVIDER_DATA_NOT_EXIST');
            return '';
        }

        const { currency } = currProviderData;

        if (currency === 'RUB') {
            title = data?.ruName || '';
        }

        if (currency === 'USD') {
            title = data?.enName || '';
        }

        return title;
    }

    const _renderProviderPaymentsMethod = (provider: IPaymentsMethods) => {
        return (
            <div className="payments-body-provider-methods">
                <div className="payments-body-provider-method">
                    <PaymentMethodCardSvg/>
                </div>
            </div>
        );
    }

    const _getProviderName = (provider: IPaymentsMethods): string => {
        let result: string = '';

        if (provider === 'stripe') {
            result = 'Stripe';
        } else if (provider === 'yookassa') {
            result = 'ЮKassa';
        } else if (provider === 'yandex-pay') {
            result = 'Яндекс Пэй';
        }

        return result;
    }

    if (!data || !settings) {
        return null;
    }

    return (
        <div className="auth" id="auth-element-id" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
            <div className="auth-container" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
                <div
                    className="auth-container"
                    style={{
                        background: !modalContent ? '#e8e8e8' : 'unset',
                        minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                    }}
                >
                    <div className="auth-modal" style={getAuthElemStyle(settings)}>
                        <Header origin="payments" paymentsData={profileData}/>
                        {_renderMenu()}
                    </div>
                </div>
            </div>
            {!modalContent && <Footer/>}
        </div>
    );
};